import type { ComponentPropsWithoutRef } from 'react';
import {
  Button,
  FieldError,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  TextField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { WindowUnit } from '../formUtils';
import { WINDOW_UNITS } from '../windowingUtils';

import styles from './WindowFieldsRow.module.css';

export interface WindowFieldsRowProps {
  windowUnit: WindowUnit;
  windowStart: number | undefined;
  windowEnd: number | undefined;
  showStartInvalid: boolean;
  showEndInvalid: boolean;
  startError?: string;
  endError?: string;
  unitLabel: WindowUnit;
  maxInUnit: number;
  onUnitChange: (unit: WindowUnit) => void;
  /** Props spread directly onto the Start <Input> (from react-hook-form register) */
  startInputProps: ComponentPropsWithoutRef<'input'>;
  /** Props spread directly onto the End <Input> (from react-hook-form register) */
  endInputProps: ComponentPropsWithoutRef<'input'>;
  className?: string;
}

export const WindowFieldsRow: React.FC<WindowFieldsRowProps> = ({
  windowUnit,
  windowStart,
  windowEnd,
  showStartInvalid,
  showEndInvalid,
  startError,
  endError,
  unitLabel,
  maxInUnit,
  onUnitChange,
  startInputProps,
  endInputProps,
  className,
}) => (
  <div className={`${styles.row}${className ? ` ${className}` : ''}`}>
    <Select
      value={windowUnit}
      onChange={(key) => onUnitChange(key as WindowUnit)}
      aria-label="Window measured in"
      className={styles.unitSelect}
    >
      <Label>Window measured in</Label>
      <Button>
        <SelectValue />
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover>
        <ListBox>
          {WINDOW_UNITS.map((unit) => (
            <ListBoxItem key={unit.id} id={unit.id}>
              {unit.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>

    <TextField isInvalid={showStartInvalid} className={styles.offsetInput}>
      <Label>Start</Label>
      <Input
        {...startInputProps}
        type="number"
        value={windowStart !== undefined && !Number.isNaN(windowStart) ? windowStart : ''}
        min={0}
        max={maxInUnit}
        step={1}
      />
      {showStartInvalid ? (
        <FieldError>{startError}</FieldError>
      ) : (
        <span className={styles.inputHint}>{unitLabel} after exposure</span>
      )}
    </TextField>

    <TextField isInvalid={showEndInvalid} className={styles.offsetInput}>
      <Label>End</Label>
      <Input
        {...endInputProps}
        type="number"
        value={windowEnd !== undefined && !Number.isNaN(windowEnd) ? windowEnd : ''}
        min={1}
        max={maxInUnit}
        step={1}
      />
      {showEndInvalid ? (
        <FieldError>{endError}</FieldError>
      ) : (
        <span className={styles.inputHint}>{unitLabel} after exposure</span>
      )}
    </TextField>
  </div>
);
